import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CalendarClock } from "lucide-react";
import { get, put } from "@/lib/api";
import { ReferentielPage } from "@/components/admin/Referentiel";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const QuartsActifsDialog = ({ ligne, onClose }) => {
  const qc = useQueryClient();
  const quarts = useQuery({ queryKey: ["quarts"], queryFn: () => get("/quarts") });
  const actifs = useQuery({ queryKey: ["quarts-actifs", ligne.id], queryFn: () => get(`/lignes/${ligne.id}/quarts-actifs`) });
  const [sel, setSel] = useState([]);
  useEffect(() => { if (actifs.data) setSel(actifs.data.quart_ids); }, [actifs.data]);
  const save = useMutation({ mutationFn: () => put(`/lignes/${ligne.id}/quarts-actifs`, { quart_ids: sel }), onSuccess: () => { toast.success("Planification enregistrée"); qc.invalidateQueries({ queryKey: ["quarts-actifs", ligne.id] }); onClose(); }, onError: (e) => toast.error(e.message) });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md rounded-sm" data-testid="quarts-actifs-dialog">
        <DialogHeader><DialogTitle className="text-base">Quarts planifiés — {ligne.nom}</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground">Les quarts cochés définissent le <b>temps requis</b> du TRS de cette ligne. Aucun quart coché = tous les quarts sont considérés actifs (comportement par défaut).</p>
        <div className="space-y-1.5">
          {(quarts.data || []).map((q) => (
            <label key={q.id} data-testid={`quart-actif-${q.id}`} className={cn("flex items-center gap-3 h-11 px-3 border rounded-sm cursor-pointer", sel.includes(q.id) ? "border-primary/60 bg-primary/10" : "border-border hover:bg-muted/40")}>
              <input type="checkbox" className="h-4 w-4" checked={sel.includes(q.id)} onChange={(e) => setSel((p) => (e.target.checked ? [...p, q.id] : p.filter((x) => x !== q.id)))} />
              <span className="font-semibold text-sm">{q.nom}</span><span className="font-mono text-xs text-muted-foreground ml-auto">{q.heure_debut} → {q.heure_fin}</span>
            </label>
          ))}
        </div>
        <div className="flex justify-end gap-2"><Button variant="outline" onClick={onClose}>Annuler</Button><Button data-testid="quarts-actifs-save" disabled={save.isPending} onClick={() => save.mutate()}>Enregistrer</Button></div>
      </DialogContent>
    </Dialog>
  );
};

export default function LignesPage() {
  const [ligne, setLigne] = useState(null);
  return (<>
    <ReferentielPage title="Lignes de production" subtitle="Lignes / machines suivies. Le bouton horloge définit les quarts réellement planifiés pour chaque ligne (base du temps requis TRS)." endpoint="lignes"
      fields={[{ key: "nom", label: "Nom", required: true, span: true, placeholder: "Ligne 1 — Ensacheuse" }, { key: "description", label: "Description", span: true }]}
      columns={[{ key: "id", header: "#", mono: true, cellClassName: "text-muted-foreground" }, { key: "nom", header: "Nom", cellClassName: "font-semibold" }, { key: "description", header: "Description", cellClassName: "text-muted-foreground" }]}
      extraActions={(r) => <button data-testid={`quarts-actifs-${r.id}`} title="Quarts planifiés" onClick={() => setLigne(r)} className="h-6 w-6 flex items-center justify-center rounded-sm hover:bg-accent text-muted-foreground hover:text-primary"><CalendarClock className="h-3.5 w-3.5" /></button>} />
    {ligne && <QuartsActifsDialog ligne={ligne} onClose={() => setLigne(null)} />}
  </>);
}
